import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChatInput } from 'features/chat/components/ChatInput';
import { ChatMessages } from 'features/chat/components/ChatMessages';
import { Globe, Images, LoaderCircle } from 'lucide-react';
import { ChatPageParams } from './hooks/useConversationById';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useChatStore } from '../../state/store/chat/use-chat-store';
import { useIsMobile } from '../../hooks/use-mobile';
import {
  Attachment,
  ButtonIds,
  FileMetadata,
  initialFeaturesState,
  Mention,
} from 'features/chat/types/chat';
import { useModelStore } from '../../state/store/chat/use-model-store';
import { useToast } from '../../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useSubscriptionStore } from 'state/store/subscription';
import { Button } from 'components/ui/button';
import { useSecureConfigStore } from 'state/store/config/useConfigStore';
import { WelcomeDialog } from 'components/blocks/welcome-dialog/welcome-dialog';
import { useSubscribeFreePlan } from 'features/subscription/hooks/use-subscription';
import { initializeCurrentPlan } from 'features/subscription/services/subscription-service';
import { useQueryClient } from '@tanstack/react-query';
import { PLANS } from 'constant/plan';
import PaymentGracePeriodModal from 'features/chat/components/PaymentGracePeriodModal';
import NameRequiredModal from 'features/chat/components/NameRequiredModal';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType } from 'analytics/events';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { getRandomGreeting } from 'utils/greeting-messages';
import { useLanguageContext } from 'i18n/language-context';
import { useInviteAssistant, useInviteUsers } from 'features/group-chat/hooks/use-invite';
import { InvitationPayload } from 'features/group-chat/services/group-services';
import { useNotificationStore } from 'state/store/common/useNotificationStore';
// import { v4 as uuidv4 } from 'uuid';
import { useGetConversationById } from 'hooks/use-conversation';
import { IamData } from '/features/iam/services/user-service';
// import { useCollaborationContext } from 'state/collaboration-websocket-middleware';
import { GroupChatMessages } from 'features/chat/components/GroupChatMessages';
import { useCollaborationSocketStore } from 'state/store/collaboration/use-collaboration-store';
// import { ConversationEvent } from 'services/conversation-services';
// import { IamData } from '/features/iam/services/user-service';

// import { useNotificationStore } from 'state/store/common/useNotificationStore';
import { useSearchParams } from 'react-router-dom';
import { getConversationById } from 'services/conversation-services';

export enum ButtonTypes {
  BUTTON,
  ICON,
}

export enum Alignment {
  LEFT,
  RIGHT,
}

export interface ChatInputButton {
  id: ButtonIds;
  label?: string;
  icon: React.ReactNode;
  onClick: () => void;
  type: ButtonTypes;
  alignment: Alignment;
  isDisabled?: boolean;
  isSelected?: boolean;
}

export default function ChatPage() {
  const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
  const currentUser = useCommonStore((state) => state.user);
  const handleGroupChatNotification = useChatStore((state) => state.handleGroupChatNotification);
  const handleStreamingMessageBatch = useChatStore((state) => state.handleStreamingMessageBatch);
  const replyMessage = useChatStore((state) => state.replyMessage);
  const lastPayloadRef = useRef<string | null>(null);
  // const lastProcessedCollaborationMessageRef = useRef<string | null>(null);
  // console.log('lastPayloadRef in chat page', lastPayloadRef.current)
  // const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const temporaryConversation = useChatStore((state) => state.temporaryConversation);
  const replaceConversationId = useChatStore((state) => state.replaceConversationId);
  const sendGroupChatStream = useChatStore((state) => state.sendGroupChatStream);
  // const handleStreamingQueue = useChatStore((state) => state.handleStreamingQueue);
  const inviteUsersMutation = useInviteUsers();

  const inviteAssistantMutation = useInviteAssistant();
  const setGroupChat = useChatStore((state) => state.setGroupChat);

  // console.log('currentUser in chat page', currentUser);
  const { t } = useTranslation();
  const location = useLocation();
  const { siteConfigurationSettings } = useSecureConfigStore();
  // const [inputButtons, setInputButtons] = useState<ChatInputButton[]>([]);
  const [isFeatureSelected, setIsFeatureSelected] = useState<Record<ButtonIds, boolean>>(() => {
    const preservedFeatures = location.state?.preservedFeatures;
    return preservedFeatures || initialFeaturesState;
  });
  const notification = useNotificationStore((state) => state.notification);
  const lastOtherMessage = useCollaborationSocketStore((state) => state.otherMessage);
  const streamingMessageBatch = useCollaborationSocketStore((state) => state.streamingMessageBatch);

  const [selectedSources, setSelectedSources] = useState<string[]>(() => {
    const preservedSources = location.state?.preservedSources;
    return preservedSources || [];
  });

  const [isSourcesToolActive, setIsSourcesToolActive] = useState<boolean>(() => {
    const preservedSourcesToolActive = location.state?.preservedSourcesToolActive;
    const preservedSources = location.state?.preservedSources;
    // If we have preserved sources tool active state, use it; otherwise infer from sources length
    return preservedSourcesToolActive !== undefined
      ? preservedSourcesToolActive
      : preservedSources && preservedSources.length > 0;
  });

  const [chatInputHeight, setChatInputHeight] = useState(186);
  // const [invitedUsers, setInvitedUsers] = useState<IamData[]>([]);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState<string>(''); // Default height
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showPaymentGracePeriodModal, setShowPaymentGracePeriodModal] = useState(false);
  const [showNameRequiredModal, setShowNameRequiredModal] = useState(false);
  const hasShownNameRequiredModalRef = useRef(false);

  const [paymentGracePeriodDays, setPaymentGracePeriodDays] = useState(0);
  const { conversationId: conversationIdFromParams } = useParams<ChatPageParams>();
  // console.log('conversationIdFromParams', conversationIdFromParams, "projectIdFromParams", projectIdFromParams)
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  // const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);

  const resetErrorByConversationId = useChatStore((state) => state.resetErrorByConversationId);
  const conversations = useChatStore((state) => state.conversations);
  const setDisableNewChat = useChatStore((state) => state.setDisableNewChat);
  console.log('conversations in chat page', conversations)

  // // Clear activeConversationId when navigating to /chat without an ID
  // useEffect(() => {
  //   if (!conversationIdFromParams && activeConversationId !== null) {
  //     setActiveConversationId(null);
  //   }
  // }, [conversationIdFromParams, activeConversationId, setActiveConversationId]);
  const prevPathnameRef = useRef<string>(location.pathname);

  const sendMessageToStore = useChatStore((state) => state.sendMessage);
  const isLoadingParticipants = useChatStore((state) => state.isLoadingParticipants);
  // console.log('isLoadingParticipants in chat page', isLoadingParticipants)
  const hasParticipants = useChatStore((state) => state.hasParticipant);
  const updateConversationTitleInStore = useChatStore((state) => state.updateConversationTitleInStore);

  const currentConversation = activeConversationId ? conversations[activeConversationId] : null;


  const cachedUsers: IamData[] = useMemo(() => {
    const unique = new Map<string, IamData>();
    const participants = currentConversation?.participants ?? [];
    const removedParticipants = currentConversation?.removedParticipants ?? [];

    [...participants, ...removedParticipants].forEach((u) => {
      if (u?.itemId && !unique.has(u.itemId)) {
        unique.set(u.itemId, u);
      }
    });

    return Array.from(unique.values());
  }, [currentConversation?.participants, currentConversation?.removedParticipants]);

  const streamingAssistantMessageId = currentConversation?.streamingAssistantMessageId;
  const messages = currentConversation?.messages ?? [];

  const isLoading: boolean = currentConversation?.isLoading ?? false;
  const isCurrentMessageLoading: boolean = currentConversation?.isCurrentMessageLoading ?? false;
  const isCancelOnGoing: boolean = currentConversation?.isCancelOnGoing ?? false;
  const isCurrentResponseGenerating: boolean =
    currentConversation?.isCurrentResponseGenerating ?? false;
  // console.log('isCurrentResponseGenerating', isCurrentResponseGenerating)
  const error = currentConversation?.error;
  const { toast } = useToast();
  const setSelectedModel = useModelStore.getState().setSelectedModel;
  const selectedModel = useModelStore((state) => state.selectedModel);
  // const userChangedModel = useModelStore((state) => state.userChangedModel);
  const isUserChangedModel = useModelStore((state) => state.isUserChangedModel);
  const models = useModelStore((state) => state.models);

  const { configurationSettings } = useConfig();
  const {
    currentPlan,
    isLoading: isCurrentPlanLoading,
    setCurrentPlan,
    setLoading,
    setError,
  } = useSubscriptionStore();
  const queryClient = useQueryClient();
  const user = useCommonStore((state) => state.user);
  const { currentLanguage } = useLanguageContext();
  const [searchParams] = useSearchParams();
  const isIncognitoChat = searchParams.get('incognito-chat') === 'true';

  useEffect(() => {
    // console.log('selectedModel in chat page', selectedModel, conversationIdFromParams)

    if (selectedModel && conversationIdFromParams && isUserChangedModel) {
      changeSelectedModel();
    }
  }, [selectedModel, isUserChangedModel]);

  const { mutate: subscribeToFreePlan, isPending: isSubscribing } = useSubscribeFreePlan({
    onSuccess: async (data) => {
      if (data.isSuccess && !hasShownWelcome) {
        await initializeCurrentPlan(configurationSettings, setCurrentPlan, setLoading, setError);
        await queryClient.invalidateQueries({ queryKey: ['current-plan'] });

        setShowWelcomeDialog(true);
        setHasShownWelcome(true);
        setShowNameRequiredModal(false);
      }
    },
    onError: () => {
      setShowWelcomeDialog(true);
    },
  });

  const fetchAndSetGroupChatParticipants = useChatStore(
    (state) => state.fetchAndSetGroupChatParticipants
  );

  useEffect(() => {
    if (location.pathname === '/chat' && location.state?.from !== 'project') {
      setIsFeatureSelected(initialFeaturesState);
      setSelectedSources([]);
      setIsSourcesToolActive(false);
    }
  }, [location.pathname, location.state]);


  // Generate random greeting message on component mount

  useEffect(() => {
    const isNavigatingToChat = location.pathname === '/chat' && prevPathnameRef.current !== '/chat';

    if (isNavigatingToChat && activeConversationId !== null) {
      useChatStore.getState().setActiveConversationId(null);
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, activeConversationId]);
  useEffect(() => {
    const randomGreeting = getRandomGreeting(user?.firstName, currentLanguage);
    setGreetingMessage(randomGreeting);
  }, [user?.firstName, currentLanguage]);

  // console.log('streamingQueue in chat page', streamingQueue)

  useEffect(() => {
    if (currentConversation && !currentConversation?.isLoading) {
      const modelName = currentConversation?.modelName || '';
      // console.log('modelName in chat page  xeeddede', currentConversation?.modelName)
      const modelIds = models.map((model) => model.id);
      const validatedModelName =
        modelName === 'auto' || modelIds.includes(modelName) ? modelName : 'auto';
      setSelectedModel(validatedModelName);
    }
  }, [currentConversation, models]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t(error),
      });
      if (activeConversationId) resetErrorByConversationId(activeConversationId);
    }
  }, [error, toast, resetErrorByConversationId, activeConversationId, t]);

  useEffect(() => {
    if (conversationIdFromParams && configurationSettings && !isIncognitoChat) {
      fetchAndSetGroupChatParticipants(
        conversationIdFromParams as string,
        configurationSettings,
        false,
        true,
      );
    }
  }, [conversationIdFromParams, configurationSettings]);

  useEffect(() => {
    if (currentPlan?.status == 'payment_pending') {
      const validUntil = new Date(currentPlan?.validUntil ?? '');
      const today = new Date();
      const diffTime = Math.abs(validUntil.getTime() - today.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPaymentGracePeriodDays(diffDays);

      setShowPaymentGracePeriodModal(true);
    }
  }, [currentPlan]);

  // Check if user has first name and last name set up
  useEffect(() => {
    if (user && !hasShownNameRequiredModalRef.current && !showWelcomeDialog && !hasShownWelcome && !isSubscribing) {
      const hasFirstName = user.firstName && user.firstName.trim() !== '';
      const hasLastName = user.lastName && user.lastName.trim() !== '';

      // Show modal if user is missing first name or last name
      if (!hasFirstName || !hasLastName) {
        setShowNameRequiredModal(true);
        hasShownNameRequiredModalRef.current = true;
      }
    }
  }, [user]);

  useEffect(() => {
    if (!lastOtherMessage) return;

    const notificationType = lastOtherMessage?.type;
    const isCurrentConversation =
      conversationIdFromParams === lastOtherMessage?.response?.conversationId;
    // console.log('Collaboration message in chat page', notificationType);


    try {
      switch (notificationType) {
        case 'CONVERSATION_CREATED':
          handleGroupChatNotification(
            lastOtherMessage,
            configurationSettings,
            notificationType,
          );
          break;
        case 'ADD_PARTICIPANT':
          if (models?.length > 0) {
            const modelIds = models.map((m) => m.id);
            const nonModelParticipants = lastOtherMessage.userIdsByType?.filter(
              (id: string) => !modelIds.includes(id)
            );

            const selectedModelId = lastOtherMessage.userIdsByType?.find((id: string) =>
              modelIds.includes(id)
            );
            // console.log('nonModelParticipants', nonModelParticipants, "isCurrentConversation", isCurrentConversation)
            if (nonModelParticipants && nonModelParticipants.length > 0) {
              if (nonModelParticipants && nonModelParticipants.length > 0) {
                const fetchLatestConversationAndUpdate = async () => {


                  const freshData = await getConversationById(lastOtherMessage?.response?.conversationId as string, configurationSettings);
                  const newSidebarEntry = {
                    ConversationId: lastOtherMessage?.response?.conversationId as string,
                    ConversationTitle: freshData?.conversationTitle ?? "",
                    CreatedDate: new Date().toISOString(),
                    LastUpdatedDate: new Date().toISOString(),
                    Conversations: [],
                    IsGroup: true,
                    TenantId: '',
                    CreatedBy: currentUser?.id ?? '',
                  };
                  useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);


                }
                fetchLatestConversationAndUpdate();
              }
            }

            if (currentUser?.id && nonModelParticipants?.includes(currentUser.id)) {
              toast({
                title:
                  t('ADDED_TO_GROUP_CHAT_TITLE') ||
                  'You have been added into a group chat',
                description:
                  t('ADDED_TO_GROUP_CHAT_DESCRIPTION') ||
                  'You have been added into a group chat',
                variant: 'success',
                duration: 3000,
              });
            }
            if (nonModelParticipants && nonModelParticipants.length > 0 && isCurrentConversation) {
              fetchAndSetGroupChatParticipants(
                conversationIdFromParams as string,
                configurationSettings,
                true
              );
              refetchConversation();

              const invitedBy = lastOtherMessage.actorId;

              // console.log('invitedBy', invitedBy)
              if (invitedBy) {
                const userIds = {
                  invitedBy,
                  invitedTo: nonModelParticipants,
                };

                handleGroupChatNotification(
                  lastOtherMessage,
                  configurationSettings,
                  notificationType,
                  userIds
                );
              }
            }
            if (
              selectedModelId &&
              selectedModel?.id !== selectedModelId &&
              conversationIdFromParams === lastOtherMessage?.response?.conversationId
            ) {
              // console.log('selectedModelId', selectedModelId)
              //   // Update conversation modelName
              useChatStore.setState((state) => ({
                conversations: {
                  ...state.conversations,
                  [conversationIdFromParams as string]: {
                    ...state.conversations[conversationIdFromParams as string],
                    modelName: selectedModelId,
                  },
                },
              }));
            }
          }
          break;

        case 'USER_ADDED_BY_TOKEN':
          if (models?.length > 0) {
            const modelIds = models.map((m) => m.id);
            const nonModelParticipants = lastOtherMessage.userIdsByType?.filter(
              (id: string) => !modelIds.includes(id)
            );

            const selectedModelId = lastOtherMessage.userIdsByType?.find((id: string) =>
              modelIds.includes(id)
            );
            if (nonModelParticipants && nonModelParticipants.length > 0) {
              const fetchLatestConversationAndUpdate = async () => {


                const freshData = await getConversationById(lastOtherMessage?.response?.conversationId as string, configurationSettings);
                const newSidebarEntry = {
                  ConversationId: lastOtherMessage?.response?.conversationId as string,
                  ConversationTitle: freshData?.conversationTitle ?? "",
                  CreatedDate: new Date().toISOString(),
                  LastUpdatedDate: new Date().toISOString(),
                  Conversations: [],
                  IsGroup: true,
                  TenantId: '',
                  CreatedBy: currentUser?.id ?? '',
                };
                useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);


              }
              fetchLatestConversationAndUpdate();
            }


            if (currentUser?.id && nonModelParticipants?.includes(currentUser.id)) {
              toast({
                title:
                  t('JOINED_GROUP_CHAT_TITLE') ||
                  'You joined the group chat',
                description:
                  t('JOINED_GROUP_CHAT_DESCRIPTION') ||
                  'You joined the group chat',
                variant: 'success',
                duration: 3000,
              });
            }
            if (nonModelParticipants && nonModelParticipants.length > 0 && isCurrentConversation) {
              fetchAndSetGroupChatParticipants(
                conversationIdFromParams as string,
                configurationSettings,
                true
              );
              refetchConversation();

              const invitedBy = lastOtherMessage.actorId;

              if (invitedBy) {
                const userIds = {
                  invitedBy,
                  invitedTo: nonModelParticipants,
                };

                handleGroupChatNotification(
                  lastOtherMessage,
                  configurationSettings,
                  notificationType,
                  userIds
                );
              }
            }
            if (
              selectedModelId &&
              selectedModel?.id !== selectedModelId &&
              conversationIdFromParams === lastOtherMessage?.response?.conversationId
            ) {
              useChatStore.setState((state) => ({
                conversations: {
                  ...state.conversations,
                  [conversationIdFromParams as string]: {
                    ...state.conversations[conversationIdFromParams as string],
                    modelName: selectedModelId,
                  },
                },
              }));
            }
          }
          break;

        case 'CONVERSATION_TITLE_UPDATED':
          handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
          break;

        case 'REMOVE_PARTICIPANT':
          handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
          break;

        case 'CONVERSATION_DELETED':
          handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
          break;
        case 'TYPING_STATUS':
          break;

        default:
          break;
      }
    } catch (err) {
      console.error('Error handling collaboration message:', err);
    }
  }, [lastOtherMessage]);

  useEffect(() => {
    if (!configurationSettings || !streamingMessageBatch || streamingMessageBatch.length === 0)
      return;

    const processBatch = async () => {
      try {
        const userMessages: any[] = [];
        const streamingMessages: any[] = [];
        const processingStartedMessages: any[] = [];

        for (const message of streamingMessageBatch) {
          if (!message) continue;

          switch (message.type) {
            case 'USER_MESSAGE':
              if (message?.response?.message?.senderId) {
                userMessages.push(message);
              }
              break;

            case 'ASSISTANT_STREAMED_RESPONSE':
              if (message?.streamingResponse?.type === 'ASSISTANT_REQUEST_PROCESSING_STARTED') {
                processingStartedMessages.push(message);
              } else {
                streamingMessages.push(message);
              }
              break;

            default:
              break;
          }
        }

        for (const msg of userMessages) {
          await handleGroupChatNotification(msg, configurationSettings, msg.type);
        }

        for (const msg of processingStartedMessages) {
          await handleGroupChatNotification(msg, configurationSettings, msg.streamingResponse.type);
        }

        if (streamingMessages.length > 0) {
          handleStreamingMessageBatch(streamingMessages, configurationSettings);
        }

        useCollaborationSocketStore.getState().clearMessages();
      } catch (err) {
        console.error('Error handling streaming message batch:', err);
      }
    };

    processBatch();
  }, [
    streamingMessageBatch,
    configurationSettings,
    handleGroupChatNotification,
    handleStreamingMessageBatch,
  ]);

  useEffect(() => {
    if (!notification?.message?.denormalizedPayload) return;

    const rawPayload = notification.message.denormalizedPayload;
    if (lastPayloadRef.current === rawPayload) {
      return;
    }
    lastPayloadRef.current = rawPayload;

    try {
      const payload = JSON.parse(rawPayload);

      if (!payload?.data?.length || typeof payload.data[0] !== 'string' || payload?.Message) return;

      const notificationData = JSON.parse(payload.data[0]);

      if (notificationData?.type === 'file_upload') {
        console.log('file_upload notification in chat page', notificationData);
      }
    } catch (err) {
      console.error('Error parsing notification payload:', err);
    }
  }, [notification]);

  const changeSelectedModel = async () => {
    const payload = {
      invitationType: 'ADD_USER' as const,
      conversationId: conversationIdFromParams,
      assistantId: selectedModel?.id,
    };
    const response = await inviteUsersMutation.mutateAsync(payload);
    if (response?.conversationId) {
      useModelStore.getState().userChangedModel(false);
    }
  };

  const handleConversationNotification = async (
    type: string,
    isCurrentConversation: boolean,
    notificationData: any
  ) => {
    // if (isCurrentConversation) {
    //   await fetchAndSetGroupChatParticipants(conversationIdFromParams as string, configurationSettings, true);
    // }
    switch (type) {
      case 'CONVERSATION_DELETED': {
        const conversationId = notificationData?.response?.conversationId;

        // Remove conversation from state if it exists
        if (conversationId) {
          const state = useChatStore.getState();
          const remainingConversations = { ...state.conversations };
          delete remainingConversations[conversationId];
          useChatStore.setState({ conversations: remainingConversations });

          // Remove from sidebar conversations list
          useChatStore.getState().removeConversationFromSidebarById(conversationId);
          useChatStore.getState().removeConversationFromSidebarProjectListById(conversationId);

          toast({
            title: t('Conversation deleted') || 'Conversation deleted',
            description: t('Conversation deleted') || 'Conversation deleted',
            variant: 'destructive',
            duration: 3000,
          });
        }

        // If it's the current conversation and the current user was involved
        if (isCurrentConversation) {
          // const isUserInvolved = currentUser?.id &&
          //   notificationData.userIdsByType?.length > 0 &&
          //   notificationData.userIdsByType.includes(currentUser.id);

          // if (isUserInvolved) {

          // }

          useChatStore.getState().setNewChat(true);
          useChatStore.getState().setActiveConversationId(null);
          navigate('/chat');
        }

        break;
      }


      case 'REMOVE_PARTICIPANT': {
        const conversationId = notificationData?.response?.conversationId;
        const isCurrentUserRemoved =
          currentUser?.id && notificationData.userIdsByType?.includes(currentUser.id);

        // If current user is removed, remove conversation from sidebar
        if (isCurrentUserRemoved && conversationId) {
          // toast({
          //   title:
          //     t('REMOVE_PARTICIPANT_TITLE') ||
          //     'You have been removed from a group chat',
          //   description:
          //     t('REMOVE_PARTICIPANT_DESCRIPTION') ||
          //     'You have been removed from a group chat',
          //   variant: 'destructive',
          //   duration: 3000,
          // });

          // Remove conversation from state
          const state = useChatStore.getState();
          const remainingConversations = { ...state.conversations };
          delete remainingConversations[conversationId];
          useChatStore.setState({ conversations: remainingConversations });

          // Remove from sidebar conversations list
          useChatStore.getState().removeConversationFromSidebarById(conversationId);
          useChatStore.getState().removeConversationFromSidebarProjectListById(conversationId);

          // If it's the current conversation, navigate away
          if (isCurrentConversation) {
            useChatStore.getState().setActiveConversationId(null);
            useChatStore.getState().setNewChat(true);
            navigate('/chat');
          }
        } else {
          // await fetchSidebarConversations(configurationSettings);
          const { data: freshData } = await refetchConversation();
          updateConversationTitleInStore(conversationId, freshData?.conversationTitle ?? "");
        }

        // If it's the current conversation, handle the notification to show system message
        if (isCurrentConversation) {
          handleGroupChatNotification(
            notificationData,
            configurationSettings,
            'REMOVE_PARTICIPANT'
          );
        }

        break;
      }
      case 'CONVERSATION_TITLE_UPDATED': {
        const conversationId = notificationData?.response?.conversationId;
        const { data: freshData } = await refetchConversation();
        updateConversationTitleInStore(conversationId, freshData?.conversationTitle ?? "");
        if (hasParticipants) {
          handleGroupChatNotification(
            notificationData,
            configurationSettings,
            'CONVERSATION_TITLE_UPDATED'
          );
        }
        break;
      }
    }
  };


  let streamingMessage = messages.find((message) => {
    if (message.role === 'assistant' && streamingAssistantMessageId) {
      return message.generations.some((generation) => generation.id === streamingAssistantMessageId);
    }
    return false;
  });


  if (!streamingMessage && isCurrentResponseGenerating) {

    const assistantMessages = messages.filter((msg) => msg.role === 'assistant');
    if (assistantMessages.length > 0) {
      const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
      const hasOngoingGeneration = lastAssistantMessage.generations.some(
        (gen) => gen.isThisGenerationOnGoing === true
      );
      if (hasOngoingGeneration || !streamingAssistantMessageId) {
        streamingMessage = lastAssistantMessage;
      }
    }
  }

  console.log('streamingMessage in chat page', streamingMessage)

  const sendMessage = async (
    content: string,
    files: Attachment[],
    sources?: string[],
    mentions?: Mention[]
  ) => {
    if (selectedModel?.id) {
      const selectedModelId = selectedModel.id;
      const alreadyExists = mentions?.some((mention) => mention.userId === selectedModelId);
      if (!alreadyExists) {
        const modelIdsSet = new Set(models?.map((model) => model.id));
        mentions = mentions?.map((mention) => {
          if (!modelIdsSet.has(mention.userId)) {
            return mention;
          }
          return {
            ...mention,
            userId: selectedModelId,
            user: {
              ...mention.user!,
              itemId: selectedModelId,
              email: selectedModelId,
              userName: selectedModelId
            }
          }
        })
      }
    }

    if (!activeConversationId) {
      // const { temporaryConversationId, recordId, assistantMessageId } = temporaryConversation(content, files, isFeatureSelected);
      const { temporaryConversationId, recordId } = temporaryConversation(
        content,
        files,
        isFeatureSelected
      );

      const payload: InvitationPayload = {
        invitationType: 'ADD_USER',
        assistantId: selectedModel?.id,
        isTemporary: isIncognitoChat ? true : false,
      };

      setGroupChat(false);
      setDisableNewChat(true);
      const response = await inviteAssistantMutation.mutateAsync(payload);
      if (response?.conversationId) {
        const newConversationId = response?.conversationId;

        // Replace the temporary conversationId with the real one
        replaceConversationId(temporaryConversationId, newConversationId, isIncognitoChat);

        trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

        // if (response) {
        let mentionUserIds: string[];
        if (!hasParticipantsArray || hasParticipantsArray?.length <= 1) {
          mentionUserIds = selectedModel?.id ? [selectedModel.id] : [];
        } else if (mentions && mentions.length > 0) {
          // Extract unique user IDs from mentions
          const userIds = new Set(mentions.map((m) => m.userId));
          mentionUserIds = Array.from(userIds);
          // console.log('mentionUserIds in sendMessage', mentionUserIds)
        } else {
          mentionUserIds = [];
        }
        const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
        const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
        const integrationSources = sources ? sources : [];

        await sendGroupChatStream(
          content,
          configurationSettings,
          newConversationId,
          files as unknown as FileMetadata[],
          replyMessage,
          mentionUserIds,
          false,
          undefined,
          imageGeneration,
          integrationSources,
          searchOnline,
          undefined,
          recordId
        );

        !isIncognitoChat ? navigate(`/chat/${newConversationId}`) : navigate(`/chat/${newConversationId}/?incognito-chat=true`);
        setDisableNewChat(false);
      } else {
        setDisableNewChat(false);
      }
    } else {
      const parentIds = replyMessage?.generation ? [replyMessage?.generation.id] : [];
      sendMessageToStore(
        content,
        configurationSettings,
        undefined,
        files,
        isFeatureSelected,
        hasParticipants,
        parentIds,
        sources,
        false,
        undefined,
        mentions,
        undefined,
        isIncognitoChat ? true : false
      );
    }
  };

  // console.log('hasParticipants in chat page', hasParticipants, "isLoadingParticipants", isLoadingParticipants, "hasParticipantsArray", hasParticipantsArray)

  //  groupChatParticipants && Object.keys(groupChatParticipants).length > 0;

  const { refetch: refetchConversation } = useGetConversationById(
    conversationIdFromParams as string,
    isIncognitoChat
  );

  const inputButtons = useMemo<ChatInputButton[]>(
    () => [
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Globe size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled:
          isFeatureSelected[ButtonIds.CREATE_IMAGE] ||
          hasParticipants ||
          selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <Images size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: hasParticipants || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
      // {
      //   id: ButtonIds.VOICE,
      //   icon: <Mic size={16} />,
      //   onClick: () => {
      //     setIsFeatureSelected((prev) => ({
      //       ...initialFeaturesState,
      //       [ButtonIds.VOICE]: !prev[ButtonIds.VOICE],
      //     }));
      //   },
      //   type: ButtonTypes.ICON,
      //   alignment: Alignment.RIGHT,
      //   isDisabled: false,
      //   isSelected: isFeatureSelected[ButtonIds.VOICE],
      // },
    ],
    [isMobile, isFeatureSelected, t, hasParticipants, selectedSources.length]
  );

  // Subscription prompt component matching the design
  const SubscriptionPrompt = () => {
    const handleViewAllPlans = () => {
      navigate('/subscription', {
        state: { from: '/chat' },
      });
    };

    const handleStartFreeTrial = () => {
      // Call the subscribeFreePlan function
      subscribeToFreePlan();
    };

    return (
      <div className="flex flex-col justify-center items-center w-full bg-white px-4">
        <div className="max-w-2xl text-center space-y-8">
          {/* Main heading */}
          <div className="space-y-4">
            <h1 className="text-3xl md:text-3xl font-bold text-foreground leading-tight">
              {t('READY_TO_CHAT_WITH_AI')}
            </h1>
            <h2 className="text-3xl md:text-3xl font-bold text-foreground">
              {t('LET_S_UNLOCK_YOUR_EXPERIENCE')}
            </h2>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xl mx-auto">
            {t('TO_START_USING_EAGLEGPT')}
          </p>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <Button
              onClick={handleViewAllPlans}
              className="bg-primary hover:bg-primary text-white px-8 py-3 rounded-lg font-semibold min-w-[140px]"
            >
              {t('VIEW_ALL_PLANS')}
            </Button>
            {!(currentPlan?.name == PLANS.NOPLAN) && <Button
              onClick={handleStartFreeTrial}
              variant="outline"
              className="border-2 border-border hover:bg-accent px-8 py-3 rounded-lg font-semibold min-w-[140px]"
              disabled={isSubscribing}
            >
              {isSubscribing ? t('STARTING_TRIAL') + '...' : t('START_FREE_TRIAL')}
            </Button>}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || isCurrentPlanLoading || isLoadingParticipants) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <LoaderCircle className="text-primary animate-spin" />
      </div>
    );
  }

  if (
    activeConversationId &&
    conversationIdFromParams &&
    activeConversationId !== conversationIdFromParams
  ) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <LoaderCircle className="text-primary animate-spin" />
      </div>
    );
  }

  // Check if subscription info should be shown (only show if explicitly true)
  const showSubscriptionInfo =
    siteConfigurationSettings?.siteConfigurations?.data?.showSubscriptionInfo === true;

  // Strict null check for subscription plan - show prompt if no plan and subscription info is enabled
  // Show subscription prompt if user has no plan (null/undefined or NOPLAN) AND subscription info is enabled
  // This ensures users without valid subscriptions see the upgrade prompt when the feature is turned on


  if ((!currentPlan || currentPlan.name == PLANS.NOPLAN) && showSubscriptionInfo && !isCurrentPlanLoading) {
    return <SubscriptionPrompt />;
  }

  return (
    <div
      className={`flex flex-col ${messages?.length === 0 ? 'justify-center' : 'justify-between'} items-center w-full`}
    >
      {messages?.length > 0 &&
        (Boolean(currentConversation?.isGroupChat) || cachedUsers.length > 1) && (
          <GroupChatMessages
            onSendMessage={sendMessage}
            messages={messages}
            isLoading={isCurrentMessageLoading}
            streamingMessage={streamingMessage}
            isStillGenerating={isCurrentResponseGenerating}
            chatInputHeight={chatInputHeight}
            currentUser={currentUser}
            participants={currentConversation?.participants || []}
            removedParticipants={currentConversation?.removedParticipants || []}
          />
        )}
      {messages?.length > 0 &&
        !(Boolean(currentConversation?.isGroupChat) || cachedUsers.length > 1) && (
          <ChatMessages
            onSendMessage={sendMessage}
            messages={messages}
            isLoading={isCurrentMessageLoading}
            streamingMessage={streamingMessage}
            isStillGenerating={isCurrentResponseGenerating}
            chatInputHeight={chatInputHeight}
            currentUser={currentUser}
          />
        )}
      {messages?.length == 0 && (
        !isIncognitoChat ? <div className="mb-8 text-center">
          <span className="text-[32px] font-normal">{greetingMessage || t('GENERAL_GREETINGS')}</span>
        </div> :
          <div className='mb-12 text-center'>
            <span className="text-[32px] font-normal">{t('INCOGNITO_CHAT')}</span>
            <p className="mt-1 text-sm text-muted-foreground max-w-md">
              {t('INCOGNITO_DESCRIPTION')}
            </p>
          </div>
      )}

      <div
        className={`flex  w-full md:w-[90%] lg:w-[67%] items-center justify-center shadow-[var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)] z-20 relative`}
      >
        <ChatInput
          onSendMessage={sendMessage}
          isLoading={isCurrentMessageLoading}
          inputButtons={inputButtons}
          isCurrentResponseGenerating={isCurrentResponseGenerating}
          onHeightChange={setChatInputHeight}
          isFeatureSelected={isFeatureSelected}
          hasParticipants={hasParticipants}
          selectedSources={selectedSources}
          onSourcesChange={setSelectedSources}
          isSourcesToolActive={isSourcesToolActive}
          onToggleTool={(toolId) => {
            setIsFeatureSelected((prev) => ({
              ...initialFeaturesState,
              [toolId]: !prev[toolId],
            }));
          }}
          onToggleSourcesTool={() => {
            // Clear all other features when sources tool is toggled
            setIsFeatureSelected(initialFeaturesState);
            setIsSourcesToolActive(!isSourcesToolActive);
          }}
          isCancelOnGoing={isCancelOnGoing}
        />
      </div>

      {/* )} */}

      {/* Welcome Dialog */}
      <WelcomeDialog
        open={showWelcomeDialog}
        onOpenChange={setShowWelcomeDialog}
        onGetStarted={() => window.location.reload()}
      />
      <PaymentGracePeriodModal
        open={showPaymentGracePeriodModal}
        onOpenChange={setShowPaymentGracePeriodModal}
        onDismiss={() => setShowPaymentGracePeriodModal(false)}
        remainingDays={paymentGracePeriodDays}
      />
      <NameRequiredModal
        open={showNameRequiredModal}
        onOpenChange={setShowNameRequiredModal}
        onDismiss={() => setShowNameRequiredModal(false)}
      />
    </div>
  );
}


